import {
  Avatar,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackProps,
  Tag,
  TagLeftIcon,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Address, FreightService, VariantType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  getPrettyFreightService,
  getPrettyQuoteSource,
  prettyCurrency,
  prettyNumber,
  QuoteStatusConfirmedLike,
  quoteStatusOptions,
} from '@pelicargo/utils';
import { Provider, QuoteSource, QuoteStatus } from '@prisma/client';
import { sortBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { HiBolt, HiBoltSlash, HiChevronDown, HiMiniTrash, HiOutlineTrophy } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useDeleteQuote } from '../../hooks/quote/useDelete';
import { quoteStatusColorScheme } from '../../utils/constants';
import { Quote } from '../../utils/types';
import { AccordionButton } from '../AccordionButton';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { AirlineIcon } from '../Icons/AirlineIcon';
import { TruckIcon } from '../Icons/TruckIcon';
import { Label } from '../LabeledText';
import { useSimpleFormContext } from '../SimpleForm';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';
import { QuoteVariantQuickActions } from './QuoteVariantQuickActions';

type Props = StackProps & {
  quote: Quote & { provider?: Provider };
  minCost: number;
  minTotalCost?: number;
  isCheapest: boolean;
  isSpotRate: boolean;
  isPivotWeight: boolean;
  isSelected: boolean;
  originAddress?: Address;
  handleSelectQuote: (quoteId: number) => void;
};

export const QuoteCardHeader = ({
  quote,
  minCost,
  minTotalCost,
  isCheapest,
  isSelected,
  isSpotRate,
  isPivotWeight,
  originAddress,
  handleSelectQuote,
  ...rest
}: Props) => {
  const { updateField } = useSimpleFormContext();
  const deleteModal = useDisclosure();
  const [connectionCount, setConnectionCount] = useState<number>(0);
  trpc.onQuoteVariantConnectionCounts.useSubscription(
    {
      quoteId: quote?.id,
    },
    {
      onData: setConnectionCount,
    },
  );

  const provider = quote?.provider;

  const stops = useMemo(
    () =>
      sortBy(quote?.flight_path || [], 'flight_path_position').reduce((acc, fs) => {
        acc.push(fs.origin_airport_id);
        if (fs.is_final_destination) acc.push(fs.destination_airport_id);
        return acc;
      }, []),
    [quote?.flight_path],
  );

  const priceColor = useMemo(() => {
    if (minCost === null) return 'gray';
    if (minCost === 0) return 'gray';
    if (isCheapest) return 'success';
    return 'subtle';
  }, [minCost, isCheapest]);

  const formattedMinCost = prettyCurrency(minCost, { isCents: false });
  const formattedMinTotalCost = prettyCurrency(minTotalCost, { isCents: false });
  const tag = useMemo(() => {
    if (minCost === null) return 'None Found';
    if (minCost === 0) return 'Missing Rates';
    if (isCheapest) return 'Cheapest';
    return null;
  }, [isCheapest, minCost]);

  const [deleteQuote, isDeleting] = useDeleteQuote(quote?.id);
  const handleQuoteDelete = async () => {
    if (!quote?.id) return;

    if (QuoteStatusConfirmedLike.includes(quote.status)) {
      deleteModal.onOpen();
    } else {
      await deleteQuote();
    }
  };

  const statusColorScheme = useMemo(() => {
    return quoteStatusColorScheme[quote.status];
  }, [quote.status]);

  const generalQuoteVariant = useMemo(() => {
    return quote?.quote_variants?.find((qv) => qv.type === VariantType.General);
  }, [quote?.quote_variants]);

  const QuickButton = useCallback(
    ({ status, label }: { status: QuoteStatus; label: string }) => {
      return (
        <Button
          size="sm"
          variant="outline"
          colorScheme={quote.status === status ? statusColorScheme : undefined}
          onClick={() => updateField('status', status)}
        >
          {label}
        </Button>
      );
    },
    [quote.status, statusColorScheme, updateField],
  );

  const isAirQuote = quote?.freight_service === FreightService.AFR;
  const defaultIcon = useMemo(() => {
    const freightService = quote?.freight_service;
    if (freightService === FreightService.AFR) return <AirlineIcon />;
    if (freightService === FreightService.EXW) return <TruckIcon />;
    if (freightService === FreightService.FCA) return <TruckIcon />;
    return <AirlineIcon />;
  }, [quote?.freight_service]);

  const path = useMemo(() => {
    if (isAirQuote) {
      return stops.join('-');
    }

    if (quote?.freight_service === FreightService.EXW) {
      return [originAddress?.zip_code, quote?.origin_airport_id].filter(Boolean).join('-');
    }
    return null;
  }, [stops, originAddress?.zip_code, quote?.origin_airport_id, quote?.freight_service, isAirQuote]);

  return (
    <Stack direction="row" justifyContent="space-between" w="full" minW="fit-content" alignItems="center" {...rest}>
      <Stack direction="row" alignItems="center" h="full" px={1} py={3} minW="240px">
        <Checkbox isChecked={isSelected} onChange={() => handleSelectQuote(quote?.id)} />
        <Stack direction="row" spacing="2" alignItems="center">
          <Avatar
            src={provider?.logo_uri}
            borderRadius="sm"
            aria-label={`${provider?.id} Logo`}
            icon={defaultIcon}
            boxSize="30px"
            opacity={useColorModeValue(1, 0.4)}
          />
          <Stack spacing={0}>
            <SimplePopoverUnifiedInput
              flex={1}
              label={
                <Stack spacing="0">
                  <Text>Q{quote?.id}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {path}
                  </Text>
                </Stack>
              }
              names={['provider.name']}
            >
              <Label label="Airline name" />
              <AirlineInput name="provider.name" hitValueKey="airline_name" />
              <Label label="Origin Airport" />
              <AirportInput name="origin_airport_id" />
              <Label label="Destination Airport" />
              <AirportInput name="destination_airport_id" />
            </SimplePopoverUnifiedInput>
          </Stack>
        </Stack>
      </Stack>

      <Show if={isAirQuote}>
        <QuoteVariantQuickActions quoteVariant={generalQuoteVariant} display={{ base: 'none', '2xl': 'flex' }} />
      </Show>

      <Stack direction={{ base: 'column', lg: 'row' }} alignItems="center" h="full" spacing={{ base: 4 }}>
        <Stack
          direction="row"
          alignItems={{ base: 'flex-end', lg: 'center' }}
          pt={{ base: 7, lg: 0 }}
          spacing="4"
          position="relative"
          h={{ base: 'initial', lg: 'full' }}
        >
          <Stack direction="row" justify="flex-end" position="absolute" top="0" right="0" flex="1">
            <Show if={!isAirQuote}>
              <Tag
                variant="subtle"
                fontWeight="500"
                size="sm"
                w="fit-content"
                borderTopRadius={0}
                title="Airline quotes connected"
              >
                <TagLeftIcon
                  as={!connectionCount ? HiBoltSlash : HiBolt}
                  color={!connectionCount ? 'muted' : 'success'}
                />
                {connectionCount}
              </Tag>
              <Tag
                variant="subtle"
                fontWeight="500"
                size="sm"
                w="fit-content"
                borderTopRadius={0}
                title={getPrettyFreightService(quote?.freight_service)}
                cursor="pointer"
              >
                {quote?.freight_service}
              </Tag>
            </Show>
            <Show if={isPivotWeight}>
              <Tag
                variant="subtle"
                colorScheme="purple"
                fontWeight="500"
                size="sm"
                borderTopRadius={0}
                title="Pivot Weight"
              >
                {`${prettyNumber(quote?.chargeable_weight)}kg`}
              </Tag>
            </Show>
            <Show if={isSpotRate && minCost > 0}>
              <Tag
                variant="subtle"
                colorScheme="blue"
                fontWeight="500"
                size="sm"
                w="fit-content"
                borderTopRadius={0}
                title="Has spot rate"
              >
                Deal
              </Tag>
            </Show>
            <Show if={!!quote?.sourced_by && quote?.sourced_by !== QuoteSource.SYSTEM}>
              <Tag
                variant="subtle"
                colorScheme="teal"
                fontWeight="500"
                size="sm"
                w="fit-content"
                borderTopRadius={0}
                title={`Sourced by ${getPrettyQuoteSource(quote?.sourced_by as QuoteSource)} API`}
              >
                {getPrettyQuoteSource(quote?.sourced_by as QuoteSource)}
              </Tag>
            </Show>
          </Stack>
          <Show if={!isNaN(minCost)}>
            <Tooltip label={tag} fontSize="md" hasArrow>
              <Stack direction="row" alignItems="center" justify="flex-end" justifySelf="center" spacing="0" w="101px">
                <Show if={isCheapest}>
                  <TagLeftIcon as={HiOutlineTrophy} color={priceColor} />
                </Show>
                <Text alignItems="center" fontSize="md" title={`${formattedMinTotalCost} with all charges`}>
                  {formattedMinCost}
                </Text>
              </Stack>
            </Tooltip>
          </Show>
        </Stack>
        <Stack direction="row" py={2} justify="flex-end" alignItems="center">
          <ButtonGroup isAttached alignItems="center">
            <QuickButton status={QuoteStatus.ReadyForQC} label="QC" />
            <QuickButton status={QuoteStatus.InProgress} label="IP" />
            <QuickButton status={QuoteStatus.SentToCarrier} label="SC" />
            <Menu>
              <Button
                size="sm"
                variant="outline"
                colorScheme={statusColorScheme}
                rightIcon={<HiChevronDown />}
                as={MenuButton}
                w="40"
                sx={{
                  '>span': { display: 'flex', justifyContent: 'space-between' },
                }}
              >
                {quoteStatusOptions.find((qso) => qso.value === quote.status)?.label}
              </Button>
              <MenuList>
                {quoteStatusOptions.map((qso) => (
                  <MenuItem key={qso.value} onClick={() => updateField('status', qso.value)}>
                    {qso.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </ButtonGroup>
          <AccordionButton />
          <IconButton
            onClick={handleQuoteDelete}
            isLoading={isDeleting}
            isDisabled={connectionCount > 0}
            title={connectionCount > 0 ? 'Cannot delete quote with connected inland quotes' : 'Delete'}
            aria-label="delete"
            icon={<HiMiniTrash />}
            borderRadius="md"
            variant="ghost"
            opacity={0}
            colorScheme="red"
            sx={{ '.quote:hover &': { opacity: 1 } }}
          />
        </Stack>
      </Stack>
      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Quote</ModalHeader>
          <ModalCloseButton />
          <ModalBody>This quote's status is {quote.status}. Are you sure you want to delete this quote?</ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={deleteQuote} isLoading={isDeleting}>
              Delete
            </Button>
            <Button variant="ghost" onClick={deleteModal.onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
